/**
 * ConversationsList Component - Phase 3 Communication System
 * Displays list of conversations in the sidebar
 */

import React from 'react';
import { Conversation } from '../../types';

interface ConversationsListProps {
  conversations: Conversation[];
  activeConversationId: string | null;
  onConversationSelect: (conversationId: string) => void;
  currentUserId: string;
}

const ConversationsList: React.FC<ConversationsListProps> = ({
  conversations,
  activeConversationId,
  onConversationSelect,
  currentUserId
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const getOtherParticipant = (conversation: Conversation) => {
    return conversation.participants.find(p => p !== currentUserId) || 'Unknown';
  };

  const getUnreadCount = (conversation: Conversation) => {
    return conversation.unreadCount?.[currentUserId] || 0;
  };

  if (conversations.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
        <p className="text-sm font-medium">No conversations yet</p>
        <p className="text-xs text-gray-400 mt-1">Start by applying to campaigns or posting new campaigns</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {conversations.map((conversation) => {
        const isActive = conversation.conversationId === activeConversationId;
        const unreadCount = getUnreadCount(conversation);
        const otherParticipant = getOtherParticipant(conversation);

        return (
          <div
            key={conversation.conversationId}
            onClick={() => onConversationSelect(conversation.conversationId)}
            className={`
              p-4 cursor-pointer transition-colors duration-150
              ${isActive
                ? 'bg-blue-50 border-r-2 border-blue-500'
                : 'hover:bg-gray-50'
              }
            `}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {otherParticipant.charAt(0).toUpperCase()}
                  </div>
                </div>

                {/* Conversation Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className={`text-sm font-medium truncate ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>
                      {otherParticipant}
                    </p>
                    {conversation.lastMessageAt && (
                      <span className={`text-xs ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
                        {formatDate(conversation.lastMessageAt)}
                      </span>
                    )}
                  </div>

                  {/* Campaign Title */}
                  {conversation.title && (
                    <div className="flex items-center space-x-1 mb-1">
                      <svg className="h-3 w-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span className="text-xs text-gray-500 truncate" title={conversation.title}>
                        {conversation.title}
                      </span>
                    </div>
                  )}

                  {/* Last Message Preview */}
                  <p className={`text-xs truncate ${isActive ? 'text-blue-700' : 'text-gray-600'}`}>
                    {conversation.lastMessageId
                      ? 'Recent activity...'
                      : 'No messages yet'
                    }
                  </p>
                </div>
              </div>

              {/* Unread Badge */}
              {unreadCount > 0 && (
                <div className="flex-shrink-0 ml-2">
                  <span className="inline-flex items-center justify-center h-5 w-5 text-xs font-medium text-white bg-red-500 rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                </div>
              )}
            </div>

            {/* Campaign/Match Status Indicator */}
            {conversation.matchId && (
              <div className="mt-2 flex items-center space-x-1">
                <div className="h-2 w-2 bg-green-400 rounded-full"></div>
                <span className="text-xs text-gray-500">Active Application</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ConversationsList;
